// Onglet admin « Skill SILAE » — le skill appris en autonomie par l'assistant.
// L'admin a un volant de secours : éditer manuellement, revenir à une version
// antérieure (revert), voir les corrections qui alimentent l'apprentissage, et
// désigner les utilisateurs « de confiance » (seules leurs corrections comptent).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAppSelector } from "@/hooks/redux";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  aiSkillAdminService,
  type SkillVersion,
  type AdminCorrection,
} from "@/services/aiSkillAdminService";

type DiffLine = { type: "ctx" | "add" | "del"; text: string };

// Diff ligne-à-ligne (LCS) entre le code généré et le code corrigé, pour que
// l'admin voie exactement ce que l'utilisateur de confiance a changé.
function lineDiff(a: string, b: string): DiffLine[] {
  const aL = a.split("\n");
  const bL = b.split("\n");
  const n = aL.length;
  const m = bL.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        aL[i] === bL[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aL[i] === bL[j]) {
      out.push({ type: "ctx", text: aL[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: "del", text: aL[i] });
      i++;
    } else {
      out.push({ type: "add", text: bL[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: aL[i++] });
  while (j < m) out.push({ type: "add", text: bL[j++] });
  return out;
}

export function AiSkillTab() {
  const users = useAppSelector((s) => s.admin.users) as Array<{
    id: string;
    email: string;
    trusted?: boolean;
  }>;

  const [versions, setVersions] = useState<SkillVersion[]>([]);
  const [active, setActive] = useState<SkillVersion | null>(null);
  const [draft, setDraft] = useState("");
  const [corrections, setCorrections] = useState<AdminCorrection[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trustedMap, setTrustedMap] = useState<Record<string, boolean>>({});
  const [diffOf, setDiffOf] = useState<AdminCorrection | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [skill, corr] = await Promise.all([
        aiSkillAdminService.getSkill(),
        aiSkillAdminService.listCorrections(false),
      ]);
      setVersions(skill.versions);
      setActive(skill.active);
      setDraft(skill.active?.content ?? "");
      setCorrections(corr.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const map: Record<string, boolean> = {};
    for (const u of users) map[u.id] = Boolean(u.trusted);
    setTrustedMap(map);
  }, [users]);

  const saveSkill = async () => {
    if (draft.trim().length === 0) return;
    setSaving(true);
    try {
      const saved = await aiSkillAdminService.saveSkill(draft);
      toast.success(`Skill enregistré (v${saved.version})`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const revert = async (id: string) => {
    try {
      const r = await aiSkillAdminService.revertSkill(id);
      toast.success(`Version ${r.version} réactivée`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec du revert");
    }
  };

  const toggleTrusted = async (userId: string, value: boolean) => {
    setTrustedMap((m) => ({ ...m, [userId]: value }));
    try {
      await aiSkillAdminService.setUserTrusted(userId, value);
      toast.success(value ? "Utilisateur de confiance activé" : "Retiré des utilisateurs de confiance");
    } catch (err) {
      setTrustedMap((m) => ({ ...m, [userId]: !value }));
      toast.error(err instanceof Error ? err.message : "Échec");
    }
  };

  const dirty = draft !== (active?.content ?? "");

  return (
    <div className="space-y-6">
      {/* Skill actif */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Règles SILAE apprises (skill actif)</span>
            {active && (
              <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                <Badge variant="outline">v{active.version}</Badge>
                <Badge variant="outline">
                  {active.source === "auto" ? "distillé auto" : "édité admin"}
                </Badge>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Ces règles sont apprises automatiquement à partir des corrections des
            utilisateurs de confiance et injectées à chaque génération. Vous pouvez
            les éditer à la main ou revenir à une version antérieure — le validateur
            SILAE reste toujours juge.
          </p>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              loading
                ? "Chargement…"
                : "Aucune règle apprise pour l'instant. Les corrections des utilisateurs de confiance alimenteront ce skill automatiquement."
            }
            className="min-h-64 font-mono text-xs"
          />
          <div className="flex items-center gap-2">
            <Button onClick={saveSkill} disabled={!dirty || saving}>
              Enregistrer (nouvelle version)
            </Button>
            {dirty && (
              <Button variant="ghost" onClick={() => setDraft(active?.content ?? "")}>
                Annuler les modifications
              </Button>
            )}
          </div>

          {versions.length > 1 && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-2">Versions</p>
              <div className="space-y-1">
                {versions.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant={v.isActive ? "default" : "outline"}>v{v.version}</Badge>
                      <span className="text-muted-foreground">
                        {v.source === "auto"
                          ? `distillé · ${v.basedOnCorrections} correction(s)`
                          : "édité admin"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.createdAt).toLocaleDateString("fr-FR")}
                      </span>
                    </span>
                    {!v.isActive && (
                      <Button size="sm" variant="outline" onClick={() => revert(v.id)}>
                        Revenir à cette version
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Utilisateurs de confiance */}
      <Card>
        <CardHeader>
          <CardTitle>Utilisateurs de confiance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Seules les corrections de ces utilisateurs (expert SILAE) alimentent
            l'apprentissage. Les éditions des autres sont enregistrées mais sans effet.
          </p>
          <div className="rounded-md border divide-y max-h-80 overflow-y-auto">
            {users.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">Aucun utilisateur.</div>
            ) : (
              users.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm">{u.email}</span>
                  <Switch
                    checked={Boolean(trustedMap[u.id])}
                    onCheckedChange={(v) => toggleTrusted(u.id, v)}
                  />
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Corrections récentes */}
      <Card>
        <CardHeader>
          <CardTitle>Corrections récentes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Confiance</TableHead>
                  <TableHead>Prime</TableHead>
                  <TableHead>Intégrée</TableHead>
                  <TableHead className="text-right">Modification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {corrections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Aucune correction pour l'instant.
                    </TableCell>
                  </TableRow>
                ) : (
                  corrections.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleDateString("fr-FR")}
                      </TableCell>
                      <TableCell className="text-sm">{c.userEmail ?? "—"}</TableCell>
                      <TableCell>
                        {c.trusted ? (
                          <Badge>confiance</Badge>
                        ) : (
                          <Badge variant="outline">standard</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{c.primeName ?? "—"}</TableCell>
                      <TableCell>
                        {c.consumed ? (
                          <Badge variant="secondary">oui</Badge>
                        ) : (
                          <Badge variant="outline">en attente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDiffOf(c)}
                        >
                          Voir le diff
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modale : ce que l'utilisateur de confiance a changé (généré → corrigé) */}
      <Dialog open={diffOf !== null} onOpenChange={(o) => !o && setDiffOf(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Modification apportée</DialogTitle>
            <DialogDescription>
              {diffOf?.primeName ? `${diffOf.primeName} — ` : ""}
              {diffOf?.userEmail ?? "utilisateur inconnu"}
              {diffOf?.trusted ? " · de confiance" : " · standard"}
            </DialogDescription>
          </DialogHeader>
          {diffOf && (
            <div className="rounded-md border bg-muted/30 overflow-x-auto max-h-[60vh]">
              <pre className="text-xs font-mono leading-relaxed">
                {lineDiff(diffOf.generatedCode, diffOf.finalCode).map((l, idx) => (
                  <div
                    key={idx}
                    className={
                      l.type === "add"
                        ? "bg-green-500/10 text-green-700 dark:text-green-400 px-3"
                        : l.type === "del"
                          ? "bg-red-500/10 text-red-700 dark:text-red-400 px-3"
                          : "text-muted-foreground px-3"
                    }
                  >
                    <span className="select-none inline-block w-4 opacity-60">
                      {l.type === "add" ? "+" : l.type === "del" ? "-" : " "}
                    </span>
                    {l.text || " "}
                  </div>
                ))}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
